import React from 'react';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { SettlementUploadHistory } from '../types/settlementUpload.types';
import { ITEMS_PER_PAGE, SETTLEMENT_UPLOAD_TYPE_LABELS } from '../constants/settlementUpload.constants';

const TABLE_HEADERS = [
    { key: 'no', label: 'No', sortable: false, width: '60px' },
    { key: 'uploadType', label: '구분', sortable: true, width: '120px' },
    { key: 'uploadDate', label: '업로드일자', sortable: true, width: '140px' },
    { key: 'applyDate', label: '적용일자', sortable: true, width: '120px' },
    { key: 'settlementPeriod', label: '정산기간', sortable: false, width: '180px' },
    { key: 'supplierCount', label: '협력사수', sortable: true, width: '100px' },
    { key: 'riderCount', label: '라이더수', sortable: true, width: '100px' },
    { key: 'deliveryFee', label: '배달료', sortable: true, width: '130px' },
    { key: 'additionalSettlement', label: '후정산', sortable: true, width: '120px' },
    { key: 'managementFee', label: '관리비', sortable: true, width: '120px' },
    { key: 'supplyAmount', label: '공급가액', sortable: true, width: '130px' },
    { key: 'vatAmount', label: '부가세액', sortable: true, width: '120px' },
    { key: 'hourlyInsurance', label: '시간제보험료', sortable: true, width: '130px' },
    { key: 'isApplied', label: '적용여부', sortable: true, width: '100px' },
    { key: 'actions', label: '관리', sortable: false, width: '100px' },
] as const;

interface SortConfig {
    columnKey: string | null;
    direction: 'asc' | 'desc' | null;
}

interface UploadHistoryTableProps {
    currentItems: SettlementUploadHistory[];
    sortedHistories: SettlementUploadHistory[];
    histories: SettlementUploadHistory[];
    currentPage: number;
    totalPages: number;
    sortConfig: SortConfig;
    applyLoading: boolean;
    onSort: (key: string) => void;
    onApplyClick: (uploadId: string) => void;
    onPageChange: (page: number) => void;
}

const formatAmount = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return '-';
    return amount.toLocaleString('ko-KR');
};

/**
 * 협력사 주정산 적용 가능 여부 확인
 * 같은 정산기간의 라이더 주정산이 적용된 후에만 협력사 주정산 적용 가능
 */
const canApplyPartnerSettlement = (
    history: SettlementUploadHistory,
    histories: SettlementUploadHistory[]
): { canApply: boolean; reason?: string } => {
    if (history.uploadType === 'RIDER') {
        return { canApply: true };
    }

    const matchingRiderSettlement = histories.find(h =>
        h.uploadType === 'RIDER' &&
        h.settlementStartDate === history.settlementStartDate &&
        h.settlementEndDate === history.settlementEndDate
    );

    if (!matchingRiderSettlement) {
        return { canApply: true };
    }

    if (!matchingRiderSettlement.isApplied) {
        return {
            canApply: false,
            reason: '같은 정산기간의 라이더 주정산을 먼저 적용해주세요'
        };
    }

    return { canApply: true };
};

const UploadHistoryTable: React.FC<UploadHistoryTableProps> = ({
                                                                   currentItems,
                                                                   sortedHistories,
                                                                   histories,
                                                                   currentPage,
                                                                   totalPages,
                                                                   sortConfig,
                                                                   applyLoading,
                                                                   onSort,
                                                                   onApplyClick,
                                                                   onPageChange,
                                                               }) => (
    <>
        {/* 검색 결과 건수 */}
        <div className="result-count">
            <i className="fas fa-list"></i>
            총 <strong>{sortedHistories.length.toLocaleString()}건</strong>
        </div>

        {/* 테이블 */}
        <div className="table-container settlement-upload-table-container">
            <table className="supplier-selection-table sortable-table">
                <thead>
                <tr>
                    {TABLE_HEADERS.map(header => {
                        const isSorted = sortConfig.columnKey === header.key;
                        const direction = isSorted ? sortConfig.direction : null;
                        return (
                            <th
                                key={header.key}
                                style={{ width: header.width, minWidth: header.width }}
                                className={header.sortable ? 'sortable-header' : ''}
                                onClick={() => header.sortable && onSort(header.key)}
                            >
                                    <span className="header-content">
                                        {header.label}
                                        {header.sortable && <SortIcon direction={direction} />}
                                    </span>
                            </th>
                        );
                    })}
                </tr>
                </thead>
                <tbody>
                {sortedHistories.length === 0 ? (
                    <tr>
                        <td colSpan={TABLE_HEADERS.length} className="text-center text-gray-600">
                            업로드 이력이 없습니다.
                        </td>
                    </tr>
                ) : (
                    currentItems.map((history, index) => {
                        const partnerCheck = canApplyPartnerSettlement(history, histories);
                        const canApply = history.canApply !== false && partnerCheck.canApply;
                        const tooltip = !partnerCheck.canApply
                            ? partnerCheck.reason
                            : history.canApply === false
                                ? '더 최신 업로드가 이미 적용되어 적용할 수 없습니다'
                                : undefined;

                        return (
                            <tr key={history.uploadId} className={history.isApplied ? 'row-applied' : ''}>
                                <td className="text-center">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                <td className="text-center">
                                        <span className={`badge badge-${history.uploadType === 'PARTNER' ? 'partner' : 'rider'}`}>
                                            {history.displayName || SETTLEMENT_UPLOAD_TYPE_LABELS[history.uploadType]}
                                        </span>
                                </td>
                                <td className="text-center">{history.uploadDate}</td>
                                <td className="text-center">{history.applyDate || '-'}</td>
                                <td className="text-center">
                                    {history.settlementStartDate} ~ {history.settlementEndDate}
                                </td>
                                <td className="text-right">{formatAmount(history.supplierCount)}</td>
                                <td className="text-right">{formatAmount(history.riderCount)}</td>
                                <td className="text-right">{formatAmount(history.deliveryFee)}</td>
                                <td className="text-right">{formatAmount(history.additionalSettlement)}</td>
                                <td className="text-right">{formatAmount(history.managementFee)}</td>
                                <td className="text-right">{formatAmount(history.supplyAmount)}</td>
                                <td className="text-right">{formatAmount(history.vatAmount)}</td>
                                <td className="text-right">{formatAmount(history.hourlyInsurance)}</td>
                                <td className="text-center">
                                        <span className={`status-badge ${history.isApplied ? 'status-applied' : 'status-pending'}`}>
                                            {history.isApplied ? '적용' : '대기'}
                                        </span>
                                </td>
                                <td className="text-center">
                                    {!history.isApplied && (
                                        <div className="action-buttons">
                                            {canApply ? (
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => onApplyClick(history.uploadId)}
                                                    disabled={applyLoading}
                                                >
                                                    적용
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    disabled
                                                    title={tooltip}
                                                >
                                                    적용 불가
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </table>
        </div>

        {/* 페이지네이션 */}
        <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
        />
    </>
);

export default UploadHistoryTable;
